import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAdminTeams, getAdminUsers } from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { adminTeamKeys } from "@/features/admin/hooks/useAdminData";
import { useMe } from "@/features/auth/useMe";
import type { AdminTeamRow, AdminUser } from "@/lib/types";
import type { TeamChoice } from "../athleteDisplay";

/**
 * Lookups the club-console athlete pages need beside the directory itself
 * (#908): the teams an athlete can be rostered on / transferred to and the
 * club's accounts for the "Link account" picker. Both reuse the keys the
 * Teams / People / Users pages already cache under (`admin-teams`,
 * `admin-users`) — unscoped-private domains, evicted at the auth boundary.
 */

export function useAdminTeamRows(enabled = true) {
  return useQuery<AdminTeamRow[]>({
    queryKey: adminTeamKeys.list(),
    queryFn: getAdminTeams,
    enabled,
    staleTime: 60_000,
  });
}

export function useAdminUserRows(enabled = true) {
  return useQuery<AdminUser[]>({
    queryKey: unscopedKey("admin-users"),
    queryFn: getAdminUsers,
    enabled,
    staleTime: 60_000,
  });
}

/** Non-archived teams the viewer may roster on, labelled with their season.
 * A club admin (or the operator on a club host) reads the whole club from
 * `GET /api/admin/teams`; anyone else gets the `/api/me` teams carrying
 * `roster:manage` — the same split the People page's invite composer uses. */
export function useRosterableTeams(enabled = true): {
  teams: TeamChoice[];
  isLoading: boolean;
} {
  const me = useMe().data;
  const canSeeClubWide = !!me?.user?.is_global_admin || !!me?.is_club_admin;
  const adminTeamsQuery = useAdminTeamRows(enabled && canSeeClubWide);
  const adminRows = adminTeamsQuery.data;
  const meTeams = me?.teams;
  const meSeasons = me?.seasons;
  const teams = useMemo(() => {
    const seasonsById = new Map((meSeasons ?? []).map((s) => [s.id, s.name]));
    const rows: TeamChoice[] = canSeeClubWide
      ? (adminRows ?? []).map((t) => ({
          id: t.id,
          name: t.name,
          seasonName: t.season_id
            ? (seasonsById.get(t.season_id) ?? t.season_id)
            : null,
          status: t.status,
        }))
      : (meTeams ?? [])
          .filter((t) => t.capabilities.includes("roster:manage"))
          .map((t) => ({
            id: t.id,
            name: t.name,
            seasonName: t.season?.name ?? null,
            status: t.status ?? "active",
          }));
    return rows
      .filter((t) => t.status !== "archived")
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [adminRows, canSeeClubWide, meSeasons, meTeams]);
  return {
    teams,
    isLoading: canSeeClubWide ? adminTeamsQuery.isLoading : !me,
  };
}
